import { asyncTimeout, cleanInputWithInitializerDeep, diffObjectsDeep } from '@caresend/utils';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { usePortalStore } from './index.js';
import { recordManager } from '../storeUtilities/recordManager/recordManager.js';
import { autoState } from '../storeUtilities/state/state.js';
import { getServiceRegionIDsByItineraryID } from '../../../../../functions/itinerary/tempHelpers.js';
import { getRouter } from '../../../../../router/helpers/initRouter.js';
import { PortalRouteName } from '../../../../../router/routes/portal/model.js';

var SessionContextID;
(function (SessionContextID) {
    SessionContextID["ITINERARY"] = "Itineraries";
})(SessionContextID || (SessionContextID = {}));
var ItineraryVersion;
(function (ItineraryVersion) {
    ItineraryVersion["LEGACY"] = "legacy";
    ItineraryVersion["NEW"] = "new";
})(ItineraryVersion || (ItineraryVersion = {}));
const useSessionStore = defineStore('sessionStore', () => {
    const activeSession = ref(null);
    const sessionLoading = ref(false);
    const router = getRouter();
    const unwatchBySessionID = {};
    const sessionInfoByID = recordManager({
        context: 'sessionInfoRecord',
        persist: true,
    })({});
    const state = autoState({
        itineraryVersion: ItineraryVersion.LEGACY,
    }, {
        persist: {
            lsKey: 'sessionStore/state',
        },
    });
    /** Watch changes to the session info */
    const initWatch = (sessionID, getter) => {
        unwatchBySessionID[sessionID] = watch(() => getter(sessionID), (newSessionInfo, oldSessionInfo) => {
            const currentSessionInfo = oldSessionInfo !== null && oldSessionInfo !== void 0 ? oldSessionInfo : sessionInfoByID.getRaw(sessionID);
            if (!newSessionInfo)
                return;
            const sessionInfo = currentSessionInfo
                ? cleanInputWithInitializerDeep(newSessionInfo, currentSessionInfo)
                : newSessionInfo;
            const diffs = diffObjectsDeep(currentSessionInfo !== null && currentSessionInfo !== void 0 ? currentSessionInfo : {}, sessionInfo);
            if (!Object.keys(diffs).length)
                return;
            sessionInfoByID.set({
                ...sessionInfo,
                id: newSessionInfo.id,
            });
        }, { deep: true, immediate: true });
    };
    const sessionsByContext = recordManager({
        context: 'sessionsByContext',
        persist: true,
    })({});
    const gettersByContextID = {
        [SessionContextID.ITINERARY]: {
            getter: usePortalStore().itineraries.getters.itinerarySession,
            dependencyTrigger: (id) => {
                getServiceRegionIDsByItineraryID(id);
            },
        },
    };
    const initSessions = () => {
        sessionsByContext.resetComputed();
        /** Retrigger and watch for all info getters if possible */
        sessionsByContext.forEach((context) => {
            context.sessions.forEach((sessionID) => {
                gettersByContextID[context.contextID].dependencyTrigger(sessionID);
                initWatch(sessionID, gettersByContextID[context.contextID].getter);
            });
        });
    };
    const setActiveSession = (sessionID) => {
        activeSession.value = sessionID;
    };
    const openRoute = async (sessionID, noRoute, params) => {
        setActiveSession(sessionID);
        if (!noRoute) {
            sessionLoading.value = true;
            await asyncTimeout(100);
            router.push({ name: PortalRouteName.ITINERARY, params });
        }
    };
    /** Indicate an itinerary session need be opened */
    const openItinerarySession = ({ itineraryID, noRoute = false, }) => {
        var _a;
        const existingContext = sessionsByContext.get(SessionContextID.ITINERARY);
        const { getter } = gettersByContextID[SessionContextID.ITINERARY];
        if (getter) {
            (_a = unwatchBySessionID[itineraryID]) === null || _a === void 0 ? void 0 : _a.call(unwatchBySessionID);
            initWatch(itineraryID, getter);
        }
        const sessions = (existingContext === null || existingContext === void 0 ? void 0 : existingContext.sessions) || new Set();
        sessions.add(itineraryID);
        if (!existingContext) {
            const newContext = {
                contextID: SessionContextID.ITINERARY,
                sessions,
                id: SessionContextID.ITINERARY,
            };
            sessionsByContext.set(newContext);
        }
        else {
            sessionsByContext.setChildOf(SessionContextID.ITINERARY, 'sessions', sessions);
        }
        openRoute(itineraryID, noRoute, {
            itineraryID,
        });
    };
    const deleteItinerarySession = async (sessionID) => {
        var _a;
        (_a = unwatchBySessionID[sessionID]) === null || _a === void 0 ? void 0 : _a.call(unwatchBySessionID);
        const context = sessionsByContext.get(SessionContextID.ITINERARY);
        if (!context)
            return;
        const { sessions } = context;
        sessions.delete(sessionID);
        sessionInfoByID.unset(sessionID);
        if (sessions.size === 0) {
            sessionsByContext.unset(SessionContextID.ITINERARY);
        }
        else {
            sessionsByContext.set({
                ...context,
                sessions,
            });
        }
        if (activeSession.value === sessionID) {
            setActiveSession(null);
            await asyncTimeout(300);
            router.push({ name: PortalRouteName.ROOT });
        }
    };
    const closeActiveSession = () => {
        activeSession.value = null;
    };
    const setSessionLoaded = () => {
        sessionLoading.value = false;
    };
    const closeAllSessions = () => {
        sessionsByContext.forEach((context) => {
            context.sessions.forEach((sessionID) => {
                deleteItinerarySession(sessionID);
            });
        });
        sessionsByContext.reset();
        sessionInfoByID.reset();
    };
    return {
        activeSession,
        closeActiveSession,
        closeAllSessions,
        deleteItinerarySession,
        initSessions,
        openItinerarySession,
        sessionInfoByID,
        sessionLoading,
        sessionsByContext,
        setActiveSession,
        setSessionLoaded,
        state,
    };
});

export { ItineraryVersion, SessionContextID, useSessionStore };
